import React, { useState, useEffect, useCallback } from "react";
import { useLoadScript, GoogleMap, InfoWindow } from "@react-google-maps/api";
import { arrayService } from "../services/array";
import { userService } from "../services/user";
import _ from "lodash";
import "../assets/styles/Map.scss";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import markerIcon from '../assets/images/homepage-map-icon.png';
import default_avatar from '../assets/images/avatar-default.png';
import location_pin_outline_icon from '../assets/images/location-pin-outline-icon.png';
import closeIcon from '../assets/images/map-marker-close-icon.png';

function HomeMap(data) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  const [activeMarker, setActiveMarker] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [infoWindow, setInfoWindow] = useState(null);
  const [mapInstance, setMapInstance] = useState(null); // Store the map instance

  const handleActiveMarker = (marker) => {
    if (marker === activeMarker) {
      setActiveMarker(null);
      return;
    }
    setActiveMarker(marker);
  };

  const initializeMarkers = useCallback(() => {
    if (!mapInstance || !data.results) return;

    const bounds = new window.google.maps.LatLngBounds();
    const newMarkers = [];

    // Generate markers from data
    data.results.forEach((item) => {
      const location = _.has(data, "location_friendly_id")
        ? arrayService.findElementByProperty(
            item.locations,
            "friendly_id",
            data.location_friendly_id
          )
        : item.location;

      const marker = new window.google.maps.Marker({
        position: { lat: parseFloat(location.lat), lng: parseFloat(location.long) },
        map: mapInstance, // Add marker to the map
        icon: {
          url: markerIcon
        }
      });

      // Add click event listener for InfoWindow
      marker.addListener("click", () => {
        setActiveMarker(marker);
        setInfoWindow(item);
      });

      newMarkers.push(marker);
      bounds.extend(marker.getPosition());
    });

    // Update markers state and clear old markers from map
    setMarkers((prevMarkers) => {
      prevMarkers.forEach((marker) => marker.setMap(null)); // Clear old markers
      return newMarkers;
    });

    // Adjust map bounds or zoom
    if (data.results.length > 1) {
      mapInstance.fitBounds(bounds);
    } else if (data.results.length === 1) {
      mapInstance.setCenter(bounds.getCenter());
      mapInstance.setZoom(16);
    }

    // Initialize MarkerClusterer
    new MarkerClusterer({
      markers: newMarkers,
      map: mapInstance,
      options: {
        maxZoom: 16,
        gridSize: 60,
      },
    });
  }, [mapInstance, data.results, data.location_friendly_id]);

  const handleOnLoad = (map) => {
    setMapInstance(map); // Save the map instance
  };

  useEffect(() => {
    initializeMarkers(); // Reinitialize markers when data changes
  }, [initializeMarkers]);

  const containerStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
  };

  const mapStyles = [
    {
      featureType: "all",
      elementType: "all",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "administrative",
      elementType: "geometry",
      stylers: [
        {
          weight: 0.5,
        },
        {
          visibility: "on",
        },
      ],
    },
    {
      featureType: "administrative",
      elementType: "labels",
      stylers: [
        {
          visibility: "simplified",
        },
      ],
    },
    {
      featureType: "administrative",
      elementType: "labels.text",
      stylers: [
        {
          lightness: -50,
        },
        {
          saturation: -50,
        },
      ],
    },
    {
      featureType: "administrative.neighborhood",
      elementType: "labels.text",
      stylers: [
        {
          hue: "#009aff",
        },
        {
          saturation: 25,
        },
        {
          lightness: 0,
        },
        {
          visibility: "simplified",
        },
        {
          gamma: 1,
        },
      ],
    },
    {
      featureType: "landscape",
      elementType: "geometry",
      stylers: [
        {
          saturation: 0,
        },
        {
          lightness: 100,
        },
        {
          gamma: 2.31,
        },
        {
          visibility: "on",
        },
      ],
    },
    {
      featureType: "landscape",
      elementType: "labels",
      stylers: [
        {
          visibility: "simplified",
        },
        {
          lightness: 20,
        },
        {
          gamma: 1,
        },
      ],
    },
    {
      featureType: "landscape",
      elementType: "labels.text.fill",
      stylers: [
        {
          saturation: -100,
        },
        {
          lightness: -100,
        },
      ],
    },
    {
      featureType: "landscape",
      elementType: "labels.text.stroke",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "landscape.man_made",
      elementType: "all",
      stylers: [
        {
          visibility: "simplified",
        },
      ],
    },
    {
      featureType: "poi",
      elementType: "all",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [
        {
          lightness: 0,
        },
        {
          saturation: 45,
        },
        {
          gamma: 4.24,
        },
        {
          visibility: "simplified",
        },
        {
          hue: "#00ff90",
        },
      ],
    },
    {
      featureType: "poi.park",
      elementType: "labels",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "road",
      elementType: "all",
      stylers: [
        {
          visibility: "on",
        },
      ],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [
        {
          saturation: -100,
        },
        {
          color: "#f5f5f5",
        },
      ],
    },
    {
      featureType: "road",
      elementType: "labels.text",
      stylers: [
        {
          visibility: "simplified",
        },
        {
          color: "#666666",
        },
      ],
    },
    {
      featureType: "road",
      elementType: "labels.icon",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "road.highway",
      elementType: "geometry.stroke",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "road.arterial",
      elementType: "geometry.stroke",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "transit",
      elementType: "labels.icon",
      stylers: [
        {
          saturation: -25,
        },
      ],
    },
    {
      featureType: "transit.line",
      elementType: "all",
      stylers: [
        {
          visibility: "simplified",
        },
      ],
    },
    {
      featureType: "transit.station.airport",
      elementType: "labels.icon",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "all",
      stylers: [
        {
          visibility: "on",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "geometry.fill",
      stylers: [
        {
          lightness: 50,
        },
        {
          gamma: 0.75,
        },
        {
          saturation: 100,
        },
      ],
    },
    {
      featureType: "water",
      elementType: "labels",
      stylers: [
        {
          visibility: "simplified",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "labels.icon",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
  ];

  return isLoaded ? (
    <GoogleMap
      onLoad={handleOnLoad}
      onClick={() => setActiveMarker(null)}
      mapContainerStyle={containerStyle}
      options={{ styles: mapStyles }}
    >
      {/* Render InfoWindow when activeMarker is set */}
      {activeMarker && infoWindow && (
        <InfoWindow
          position={activeMarker.getPosition()}
          onCloseClick={() => setActiveMarker(null)}
        >
          <div className={`marker-container ${data.parentName}`}>
            <button className="btn close-marker-btn" onClick={() => setActiveMarker(null)} title="Close">
              <img src={closeIcon}/>
            </button>
            <div className="marker-info-container text-center">
              <div className="profile-pic">
                <img src={`${infoWindow.avatar ? (infoWindow.avatar.url.thumb.url) : default_avatar}`} onError={(e) => { e.target.src = default_avatar }} alt="profile-pic"/>
              </div>
              <div className="marker-info-name">{infoWindow.name}</div>
              <div className="marker-info-specialty">{infoWindow.specialties[0].name}</div>
              <a className="marker-info-location" href={`/search-book/clinic/${infoWindow.location.friendly_id}`} target="_blank" rel="noopener noreferrer">
                <img className="location-pin-outline-icon" src={location_pin_outline_icon} alt='location-pin-icon' />
                {infoWindow.location.name}
              </a>
            </div>
            {data.parentName === "ResultMapView" && (
              <div className="button-group-2">
                {userService.getUser() && (
                  <a className="btn btn-call" href={`tel:${infoWindow.phone_number}`}>Call</a>
                )}
                <a className="btn btn-view" href={`/search-book/professional-profile/${infoWindow.friendly_id}/${infoWindow.location.friendly_id}`} target="_blank" rel="noopener noreferrer">View</a>
              </div>
            )}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  ) : (
    <div>Loading...</div>
  );
}

export default HomeMap;
